import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../../firebase/AuthProvider';
import EventNotification from '../../../interfaces/eventNotification/EventNotification';
// Import the new service function
import { getNotifications, markNotificationRead, markAllNotificationsRead } from '../../../services/notificationServices/NotificationService';
import Loader from '../loader/Loader';
import './Notificationtab.css';
import { getTimeAgo } from '../../../utilities/dateUtils';
import AvatarImage from '../../ui/Avatar/AvatarImage';
import { renderNotificationMessage, renderNotificationTitle } from '../../../utilities/notifications/notificationUtils';
import { useNotificationState } from '../../../contexts/NotificationStateContext';
import { Button } from 'react-bootstrap';
import yourNotificationImage from "../../../assets/images/NotificationImage.svg";
import READ from "../../../assets/images/READ.svg";
import NotificationDropdown from './NotificationDropdown';



const PAGE_SIZE = 20;

const Notificationtab = () => {
    const { currentUser } = useAuth();
    const { markAsAllReadNotification, markNotificationAsReadById } = useNotificationState();
    const [notifications, setNotifications] = useState<EventNotification[]>([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [isLoading, setIsLoading] = useState(true);
    const [isMoreLoading, setIsMoreLoading] = useState(false);

    // A memoized value to efficiently check if any notifications are unread
    const hasUnreadNotifications = useMemo(() => notifications.some(n => !n.read), [notifications]);

    const fetchNotifications = useCallback(async (currentPage: number) => {
        if (!currentUser) return;
        currentPage === 1 ? setIsLoading(true) : setIsMoreLoading(true);

        try {
            const authToken = await currentUser.getIdToken();
            const response = await getNotifications(authToken, currentPage, PAGE_SIZE);

            if (response) {
                const combined = currentPage === 1 ? response : [...notifications, ...response];
                const uniqueNotifications = Array.from(new Map(combined.map(item => [item.id, item])).values());
                setNotifications(uniqueNotifications);
                if (response.length < PAGE_SIZE) setHasMore(false);
            } else {
                setHasMore(false);
            }
        } catch (error) {
            console.error("Failed to fetch notifications:", error);
            setHasMore(false);
        } finally {
            setIsLoading(false);
            setIsMoreLoading(false);
        }
    }, [currentUser, notifications]);

    useEffect(() => {
        setNotifications([]);
        setPage(1);
        setHasMore(true);
        fetchNotifications(1);
    }, [currentUser]);

    const handleViewMore = () => {
        const nextPage = page + 1;
        setPage(nextPage);
        fetchNotifications(nextPage);
    };

    const totalNotifications = notifications.length;
    const unreadCount = notifications.filter(n => !n.read).length;

    const handleNotificationClick = async (notification: EventNotification, e: React.MouseEvent<HTMLDivElement>) => {
        if ((e.target as HTMLElement).closest('a')) return;
        if (!notification.read && currentUser) {
            const authToken = await currentUser.getIdToken();

            // read from notification as well
            markNotificationAsReadById(notification.id);

            const success = await markNotificationRead(authToken, notification.id);
            if (success) {
                setNotifications(prev => prev.map(n => n.id === notification.id ? { ...n, read: true } : n));
            }
        }
    };

    // Handler for the new "Mark all as read" button
    const handleMarkAllRead = async () => {
        if (!currentUser || !hasUnreadNotifications) return;

        try {
            const authToken = await currentUser.getIdToken();
            const success = await markAllNotificationsRead(authToken);
            //refresh the notification from header
            markAsAllReadNotification();
            if (success) {
                // If the API call succeeds, update the local state to reflect the change
                setNotifications(prev => prev.map(n => ({ ...n, read: true })));
            } else {
                console.error("Server failed to mark all notifications as read.");
            }
        } catch (error) {
            console.error("Error marking all notifications as read:", error);
        }
    };

    if (isLoading) {
        return <Loader />;
    }

    return (
        <div className="notification-page-container mby_t_24">
            {/* Header section with the title and the new button */}
            <div className="flex justify-between mb-3 items-center">
                <p className="text-[#6A7282] text-lg font-poppins m-0">
                    {totalNotifications} notifications • {unreadCount} unread
                </p>
                <div className='flex items-center flex-wrap gap-3'>
                    <NotificationDropdown />
                    {hasUnreadNotifications && (
                        <Button variant="primary" className='btn_sm !font-normal !rounded-[10px] px-1' onClick={handleMarkAllRead}>
                            <img
                                src={READ}
                                alt='Mark-read'
                                className='w-[16px] h-[16px]'
                            />
                            Mark all as read
                        </Button>
                    )}
                </div>
            </div>

            <div className="space-y-5">
                {notifications.length > 0 ? (
                    notifications.map(notification => (
                        // <div
                        //     key={notification.id}
                        //     className={`notification-item m-0 ${!notification.read ? 'unread' : 'read'}`}
                        //     onClick={(e) => handleNotificationClick(notification, e)}
                        // >
                        //     {/* ... notification item content ... */}
                        //     <div className="notification-avatar">
                        //         {notification.createdByUser ? (
                        //             <AvatarImage userId={notification.createdByUser.id} size={"xs"} />
                        //         ) : (
                        //             <div className="default-avatar-icon">
                        //                 <FontAwesomeIcon icon={faBell} />
                        //             </div>
                        //         )}
                        //     </div>
                        //     <div className="notification-content">
                        //         <div className="notification-text">
                        //             {renderNotificationMessage(notification)}
                        //         </div>
                        //         <span className="notification-time">{getTimeAgo(notification.createdAt)}</span>
                        //     </div>
                        //     {!notification.read && <div className="unread-dot"></div>}
                        // </div>
                        <div
                            key={notification.id}
                            onClick={(e) => handleNotificationClick(notification, e)}
                            className={`flex !items-start justify-between w-full p-4 rounded-[10px] 
              shadow-[0_1px_2px_-1px_rgba(0,0,0,0.1),0_1px_8px_rgba(0,0,0,0.1)]
              bg-white border-l-2 
              ${!notification.read ? 'border-[#7E57C2]' : 'border-[#d5c8f0]'}
              cursor-pointer transition hover:bg-gray-50`}
                        >
                            {/* Left Section */}
                            <div className="!flex !justify-start !items-start gap-4">
                                <div className="w-10 min-w-10 h-10 min-h-10 rounded-full bg-[#F3F4F6] flex items-center justify-center overflow-hidden">
                                    {notification.createdByUser ? (
                                        <AvatarImage userId={notification.createdByUser.id} size={"xs"} />
                                    ) : (
                                        <img
                                            src={yourNotificationImage}
                                            alt="notification"
                                            className="w-[40px] h-[40px]"
                                        />
                                    )}
                                </div>
                                <div className="flex flex-col !justify-start !items-start">
                                    <p className="text-[#101828] m-0 text-start font-poppins font-medium text-[18px] leading-[20px]">
                                        {renderNotificationTitle(notification)}
                                    </p>

                                    <p className="text-[#676767] w-[90%] mb-0 mt-[12px] text-start font-poppins font-normal text-[14px] leading-[16px]">
                                        {renderNotificationMessage(notification)}
                                    </p>
                                </div>
                            </div>
                            <span className="text-[#99A1AF] font-poppins font-normal text-[14px] whitespace-nowrap">
                                {getTimeAgo(notification.createdAt)}
                            </span>
                        </div>

                    ))
                ) : (
                    <div className="empty-notifications">
                        <div className="empty-icon">✨</div>
                        <h3>You're all caught up</h3>
                        <p>All notifications will be displayed here</p>
                    </div>
                )}
            </div>

            {hasMore && (
                <div className="view-more-container text-[#776CE6] text-center my-2 text-lg">
                    {isMoreLoading ? (
                        <div>
                            Loading...
                        </div>
                    ) : (
                        <div
                            onClick={handleViewMore}
                            className='cursor-pointer hover:underline inline'>
                            View More
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default Notificationtab;